// src/data/ratification.js
//
// WO-001 S10 (2026-09-14, task list section 249). Design A3.4.2, from SMART Constitution SS85 lines
// 33-47 as read on 2026-08-17: a SYSTEM agreement - the General Chairperson "must poll the entire
// membership holding seniority and working in the craft involved on the property by mail referendum
// ballot prior to signing," governed by the majority of votes cast; a LOCAL agreement - "must poll
// the affected Local Chairpersons prior to signing"; either way a report of the result goes to each
// affected Local within THIRTY DAYS. "A platform that records agreements as governing documents,
// while being silent about whether the constitutionally required vote happened, is missing the step
// that makes the document binding."
//
// So every VERSION of an agreement carries its ratification record - or reads UNRECORDED, in those
// words. This module is the record's shape and the checks: the poll came before the signature; the
// result is the majority of votes cast; the report to the Locals has a date or is OVERDUE thirty
// days after the result. Pure; Agreements.js writes what planRatification returns onto the version.

import { lastDayOf } from './timeLimit';   // ruling 314 (forensic audit 2026-10-16)

export const RATIFICATION_KINDS = {
  system: { kind: 'system', label: 'System agreement', method: 'mail referendum ballot of the entire membership holding seniority and working in the craft on the property', clause: 'SS85 lines 33-41' },
  local:  { kind: 'local',  label: 'Local agreement',  method: 'poll of the affected Local Chairpersons', clause: 'SS85 lines 42-47' },
  none:   { kind: 'none',   label: 'No ratification required', method: null, clause: null },   // e.g. a carrier-imposed or national document held for reference - the custodian says so, on the record
};
export const REPORT_DAYS = 30;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const isDate = (s) => typeof s === 'string' && DATE_RE.test(s) && !Number.isNaN(Date.parse(s + 'T00:00:00Z'));
// Forensic audit 2026-10-16: the report is due within 30 days of the result, and ruling 314 makes the result's day day
// one - so the 30th day is the last (timeLimit.lastDayOf). The old addDays(polledOn, 30) put it one day late.

export function planRatification(input) {
  const i = input || {};
  const errors = [];
  const k = RATIFICATION_KINDS[i.kind];
  if (!k) errors.push('kind must be system, local or none');
  if (!i.recordedBy) errors.push('no recorder');
  if (k && k.kind === 'none') {
    if (!String(i.reason || '').trim()) errors.push('"no ratification required" is a claim about the constitution - say why, on the record');
    if (errors.length) return { ok: false, errors };
    return { ok: true, errors: [], record: { kind: 'none', reason: String(i.reason).trim(), recordedBy: i.recordedBy, recordedByName: i.recordedByName || null, recordedAt: i.at || new Date().toISOString() } };
  }
  if (!isDate(i.polledOn)) errors.push('polledOn (the date the poll closed / the result was known) is required, YYYY-MM-DD');
  const yes = Number(i.votesFor), no = Number(i.votesAgainst);
  if (!Number.isInteger(yes) || yes < 0 || !Number.isInteger(no) || no < 0) errors.push('votes for and against are whole numbers - the majority of votes cast governs (SS85)');
  if (Number.isInteger(yes) && Number.isInteger(no) && yes + no === 0) errors.push('no votes were cast - that is not a ratification');
  if (i.signedOn != null && i.signedOn !== '' && !isDate(i.signedOn)) errors.push('signedOn must be YYYY-MM-DD or empty (not yet signed)');
  if (isDate(i.polledOn) && isDate(i.signedOn) && i.signedOn < i.polledOn) errors.push('SS85: the poll comes PRIOR TO signing - an agreement signed ' + i.signedOn + ' before the poll closed ' + i.polledOn + ' is recorded as such, not as ratified (record the poll date as it was; the flag stands on the record)');
  if (i.reportedOn != null && i.reportedOn !== '' && !isDate(i.reportedOn)) errors.push('reportedOn must be YYYY-MM-DD or empty (not yet reported)');
  if (isDate(i.polledOn) && isDate(i.reportedOn) && i.reportedOn < i.polledOn) errors.push('the report to the Locals cannot precede the result');
  if (errors.length) return { ok: false, errors };
  const result = yes > no ? 'ratified' : yes < no ? 'rejected' : 'tied - not ratified (no majority)';
  return {
    ok: true, errors: [],
    record: {
      kind: k.kind, method: k.method, clause: k.clause,
      polledOn: i.polledOn, votesFor: yes, votesAgainst: no, ballotsCast: yes + no, result,
      signedOn: isDate(i.signedOn) ? i.signedOn : null,
      reportedOn: isDate(i.reportedOn) ? i.reportedOn : null, reportDueBy: lastDayOf(i.polledOn, REPORT_DAYS),
      evidence: String(i.evidence || '').trim() || null, note: String(i.note || '').trim() || null,
      recordedBy: i.recordedBy, recordedByName: i.recordedByName || null, recordedAt: i.at || new Date().toISOString(),
    },
  };
}

// What a screen says about a version's ratification, today.
export function ratificationState(version, today) {
  const r = version && version.ratification;
  const t = isDate(today) ? today : new Date().toISOString().slice(0, 10);
  if (!r) return { state: 'unrecorded', tone: 'warn', line: 'Ratification UNRECORDED - SS85 requires a poll prior to signing and a report to the Locals within 30 days; nothing is on the record for this version.' };
  if (r.kind === 'none') return { state: 'none', tone: 'dim', line: 'No ratification required, per the custodian: ' + r.reason };
  const parts = [RATIFICATION_KINDS[r.kind] ? RATIFICATION_KINDS[r.kind].label : r.kind, r.result.toUpperCase() + ' ' + r.polledOn + ' (' + r.votesFor + ' for, ' + r.votesAgainst + ' against, ' + r.ballotsCast + ' cast)'];
  let tone = r.result === 'ratified' ? 'ok' : 'bad', state = r.result;
  const flags = [];
  if (r.signedOn) { parts.push('signed ' + r.signedOn); if (r.signedOn < r.polledOn) { flags.push('SIGNED BEFORE THE POLL - SS85 requires the poll prior to signing'); tone = 'bad'; } }
  else parts.push('not yet signed');
  if (r.reportedOn) parts.push('reported to the Locals ' + r.reportedOn + (r.reportedOn > r.reportDueBy ? ' (LATE - due ' + r.reportDueBy + ')' : ''));
  else if (t > r.reportDueBy) { flags.push('REPORT TO THE LOCALS OVERDUE - due ' + r.reportDueBy + ' (30 days from the result)'); tone = 'bad'; state = 'report_overdue'; }
  else parts.push('report to the Locals due by ' + r.reportDueBy);
  return { state, tone, line: parts.join(' - ') + (flags.length ? ' - ' + flags.join('; ') : ''), flags };
}

// The custodian records it; nobody else. Same rule as the agreement's update rule (custodian GC or admin).
export function mayRecordRatification(user, agreement) {
  if (!user || !agreement) return false;
  if (user.isAdmin || user.role === 'admin') return true;
  return user.role === 'general_chair' && !!user.committee && user.committee === (agreement.custodianCommittee || agreement.committee);
}

// The versions array with the record on one version - what Agreements.js writes back.
export function versionsWithRatification(versions, versionNo, record) {
  const list = Array.isArray(versions) ? versions : [];
  if (!list.some(v => v && v.version === versionNo)) return { ok: false, errors: ['version ' + versionNo + ' is not on the record'] };
  return { ok: true, errors: [], versions: list.map(v => (v && v.version === versionNo) ? { ...v, ratification: record } : v) };
}
